import React, { useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoHeart, IoHeartOutline } from "react-icons/io5";
import { mdiDotsHorizontal, mdiRemoteTv } from "@mdi/js";
import Main from "../../components/layout/Main";
import AppDeviceCard from "../../components/device/app/AppDeviceCard";
import MdiIcon from "../../components/device/app/MdiIcon";
import { useToggleTop } from "../../components/common/ToggleTop";
import { usePropertyRole } from "../../hooks/usePropertyOwner";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import { uniqueRemotes } from "./remoteUtils";
import "../../assets/styles/app-device.css";

// Remotes learned on one IR blaster (app/screens/devices/RemotesList.js), with
// the blaster's own favourite heart and its "⋯" Edit Device, as on other
// devices' pages. The list is read again from the config, so a remote renamed
// or deleted from its page shows as it is now. Location state: { data: the IRBM device }.
function RemotesList() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const role = usePropertyRole();
  const device = state?.data;
  const [favourite, setFavourite] = useState(Boolean(device?.usersFavourite?.includes(User.getUser())));
  const [menuOpen, setMenuOpen] = useState(false);
  const [saved, setSaved] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!device) return undefined;
    let live = true;
    UserService.getConfig(User.getPropertyId())
      .then((config) => {
        const found = config?.devices?.find((item) => String(item.applianceId) === String(device.applianceId));
        if (live && found) setSaved(found);
      })
      .catch(() => {
        // The remotes it was opened with stay on screen.
      });
    return () => {
      live = false;
    };
  }, [device]);

  // Close the more menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown" ? event.key === "Escape" : !menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  const remotes = useMemo(() => uniqueRemotes((saved ?? device)?.remoteDevices), [saved, device]);

  if (!device) return <Redirect to="/dashboard" />;

  const toggleFavourite = async () => {
    const next = !favourite;
    setFavourite(next);
    try {
      await UserService.userFav({ applianceId: device.applianceId, favourite: next }, User.getPropertyId());
      toast(next ? "Added to favourites" : "Removed from favourites", "green");
    } catch (err) {
      setFavourite(!next);
      toast("Couldn't update favourites. Please try again.", "red");
    }
  };

  const editDevice = () => {
    setMenuOpen(false);
    if (role === "guest") {
      toast("You have to be a owner to edit the device.", "red");
      return;
    }
    history.push("/editDevice", { data: device });
  };

  const moreMenu = (
    <div ref={menuRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="hx-round"
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <MdiIcon path={mdiDotsHorizontal} size={28} />
      </button>
      {menuOpen ? (
        <div className="hx-pop" role="menu">
          <button type="button" role="menuitem" onClick={editDevice}>
            Edit Device
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Main header="stack" title={device.name} headerRight={moreMenu}>
      <div className="hx-detail-actions">
        <button
          type="button"
          className="hx-round is-small"
          onClick={toggleFavourite}
          aria-pressed={favourite}
          aria-label={favourite ? "Remove from favourites" : "Add to favourites"}
        >
          {favourite ? <IoHeart style={{ color: "red" }} /> : <IoHeartOutline />}
        </button>
      </div>
      {remotes.length ? (
        <>
          <h2 className="hx-section-title">Remotes</h2>
          <div className="hx-grid">
            {remotes.map((remote, index) => {
              const name = remote.name || `${remote.deviceType ?? ""} remote`.trim();
              return (
                <AppDeviceCard
                  key={remote.remoteId ?? index}
                  name={name}
                  floor={device.floor}
                  room={device.room}
                  type={remote.deviceType}
                  active={false}
                  centerIcon
                  onOpen={() => history.push("/remote", { data: device, remote })}
                  ariaLabel={`${name}${remote.brand ? `, ${remote.brand}` : ""}. Open remote`}
                />
              );
            })}
          </div>
        </>
      ) : (
        <div className="hx-empty-page">
          <MdiIcon path={mdiRemoteTv} size={120} />
          <p>No remotes added</p>
          <span>Add remotes to this IR blaster from Universal Remotes, in the + menu.</span>
        </div>
      )}
    </Main>
  );
}

export default RemotesList;
